<template>
  <div class="form-item form-type-select form-item-municipality">
    <label class="form-required form-label" for="municipality-contact-form">
      {{ $t("Municipality") }}
      <span class="required-marker">*</span>
    </label>
    <multiselect
      v-model="selectedMunicipality"
      track-by="uuid"
      label="name"
      :placeholder="$t('-- Select --')"
      :options="Object.values(municipalities ?? {})"
      :searchable="true"
      :select-label="$t('Select')"
      :selected-label="$t('Selected')"
      :deselect-label="$t('Remove')"
      :disabled="isLocked || loadFailed"
      :required="true"
    >
      <template v-slot:noResult>{{ $t("No results found") }}</template>
      <template v-slot:noOptions>{{ $t("List is empty") }}</template>
      <template v-slot:singleLabel="{ option }">
        {{ option.name }}
      </template>
    </multiselect>
  </div>

  <div class="form-item form-type-select form-item-franchisee">
    <label class="form-required form-label" for="franchisee-contact-form">
      {{ $t("Credit Intermediary") }}
      <span class="required-marker">*</span>
    </label>
    <div class="franchisee-select-wrapper">
      <multiselect
        id="franchisee-contact-form"
        v-model="selectedFranchisee"
        track-by="franchisee_id"
        label="commercial_name"
        :placeholder="!selectedMunicipality?.uuid ? $t('-- Select a municipality --') : $t('-- Select --')"
        :options="filteredFranchisees"
        :searchable="false"
        :select-label="$t('Select')"
        :selected-label="$t('Selected')"
        :deselect-label="$t('Remove')"
        :disabled="isLocked || loadFailed || !selectedMunicipality?.uuid || !filteredFranchisees.length"
        :required="true"
      >
        <template v-slot:noResult>{{ $t("No results found") }}</template>
        <template v-slot:noOptions>{{ $t("List is empty") }}</template>
        <template #singleLabel="props">
          <span class="option__desc">
            <span class="option__title-selected">{{ props.option.commercial_name }}</span>
          </span>
        </template>
        <template #option="props">
          <img class="option__image" :src="props.option.photo" :alt="props.option.legal_name" />
          <div class="option__desc">
            <span class="option__title">{{ props.option.commercial_name }}</span>
            <span class="fs-small option__address">{{ props.option.address }}</span>
          </div>
        </template>
      </multiselect>
      <button
        v-if="locked"
        type="button"
        class="btn btn-simple"
        :disabled="!isLocked"
        @click="dialogRef.showModal()"
      >
        {{ $t("Edit") }}
      </button>
    </div>
    <input-error v-if="loadFailed" :messages="[$t('Could not load the credit intermediaries list. Please try again later.')]" />
    <input-error :messages="errors?.franchisee_id"/>
  </div>

  <input type="hidden" name="municipality_id" :value="selectedMunicipality?.id" />
  <input type="hidden" name="franchisee_id" :value="selectedFranchisee?.franchisee_id" />

  <dialog v-if="locked" ref="dialogRef" class="dialog" role="dialog">
    <div class="stack | dialog__body">
      <h3>{{ $t("Change franchisee") }}</h3>
      <p>{{ $t("Are you sure you want to change the franchisee of this request?") }}</p>
      <label class="form-checkbox">
        <input type="checkbox" v-model="changeConfirmed" />
        <span>{{ $t("I accept that changing the franchisee implies transferring all my data to the new franchisee and accepting a new RGPD.") }}</span>
      </label>
      <div class="flex">
        <button type="button" class="btn-secondary" autofocus @click="cancelEdit">{{ $t("Cancel") }}</button>
        <button type="button" class="btn-primary" :disabled="!changeConfirmed" @click="confirmEdit">{{ $t("Confirm") }}</button>
      </div>
    </div>
  </dialog>
</template>

<script setup>
import InputError from "@/components/InputError.vue";
import { ref, computed, onBeforeMount } from "vue";
import { storeToRefs } from "pinia";
import { useFranchiseeSelectorList } from "@/stores/useFranchiseeSelectorList.js";

import Multiselect from "vue-multiselect";

const props = defineProps({
  errors: {
    type: Object,
    default: () => ({}),
  },
  defaultFranchiseeId: {
    type: Number,
  },
  updatedOnState: {
    type: Number,
    required: true,
  },
  locked: {
    type: Boolean,
    default: false,
  },
});

const simulatorStore = useFranchiseeSelectorList();
const { franchisees, municipalities, loadFailed } = storeToRefs(simulatorStore);

const selectedMunicipality = ref(null);
const selectedFranchisee = ref(null);
const isLocked = ref(props.locked);
const dialogRef = ref(null);
const changeConfirmed = ref(false);

const confirmEdit = () => {
  isLocked.value = false;
  changeConfirmed.value = false;
  dialogRef.value.close();
};

const cancelEdit = () => {
  changeConfirmed.value = false;
  dialogRef.value.close();
};

const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }

  return array;
};

const filteredFranchisees = computed(() => {
  if (!selectedMunicipality.value) {
    return [];
  }

  const franchiseesList = Object.values(franchisees.value).filter(
    (franchisee) => franchisee.municipality_uuid === selectedMunicipality.value.uuid
  );

  return shuffleArray(franchiseesList);
});

onBeforeMount(async () => {
  await simulatorStore.fetchFranchisees(props.updatedOnState);

  const franchisee = Object.values(franchisees.value ?? {}).find(
    (candidate) => Number(candidate.franchisee_id) === Number(props.defaultFranchiseeId)
  );

  const municipality = franchisee
    ? Object.values(municipalities.value ?? {}).find(
        (candidate) => candidate.uuid === franchisee.municipality_uuid
      )
    : null;

  if (franchisee && municipality) {
    selectedFranchisee.value = franchisee;
    selectedMunicipality.value = municipality;
  } else {
    isLocked.value = false;
  }

  if (loadFailed.value) {
    window.dispatchEvent(new CustomEvent("franchisee-list-unavailable"));
  }
});

</script>
